import { ACTION_GROUPS, ALL_ACTIONS, TOGGLE_ACTIONS, VALUE_ACTIONS } from "./defaults.js";
import { clamp, toCamelCase } from "./utils.js";

const HTML_CLASS_ACTIONS = {
  darkContrast: "rl-dark-contrast",
  lightContrast: "rl-light-contrast",
  monochrome: "rl-monochrome",
  highSaturation: "rl-high-saturation",
  highContrast: "rl-high-contrast",
  lowSaturation: "rl-low-saturation",
  contentScaling: "rl-content-scaling",
};

const BODY_CLASS_ACTIONS = {
  fontSizing: "rl-font-sizing",
  lineHeight: "rl-line-height",
  letterSpacing: "rl-letter-spacing",
  readableFont: "rl-readable-font",
  dyslexiaFont: "rl-dyslexia-font",
  alignLeft: "rl-align-left",
  alignCenter: "rl-align-center",
  alignRight: "rl-align-right",
  highlightTitles: "rl-highlight-titles",
  highlightLinks: "rl-highlight-links",
  highlightHover: "rl-highlight-hover",
  highlightFocus: "rl-highlight-focus",
  hideImages: "rl-hide-images",
  hideEmoji: "rl-hide-emoji",
  bigBlackCursor: "rl-big-black-cursor",
  bigWhiteCursor: "rl-big-white-cursor",
};

const VALUE_LIMITS = {
  contentScaling: { min: -50, max: 200 },
  fontSizing: { min: -4, max: 14 },
  lineHeight: { min: -30, max: 120 },
  letterSpacing: { min: -2, max: 12 },
};

export function createActionController(config, deps) {
  const state = structuredClone(config.initialState);
  const runtime = {
    readingMask: {
      top: null,
      bottom: null,
      handler: null,
    },
    readingGuide: {
      node: null,
      handler: null,
    },
    dyslexiaStyle: null,
    dyslexiaPreloadStarted: false,
  };

  // Inject dyslexia faces as soon as the widget starts so the font is already available.
  ensureDyslexiaFaces();

  function load(loadedState) {
    if (!loadedState) {
      return;
    }
    if (loadedState.values) {
      for (const key of VALUE_ACTIONS) {
        const value = Number(loadedState.values[key]);
        if (!Number.isNaN(value)) {
          state.values[key] = value;
        }
      }
    }
    if (loadedState.toggles) {
      for (const key of TOGGLE_ACTIONS) {
        state.toggles[key] = Boolean(loadedState.toggles[key]);
      }
    }
  }

  function snapshot() {
    return structuredClone(state);
  }

  function dispatch(rawAction, value, meta = {}) {
    const action = normalizeAction(rawAction);
    if (!action || !ALL_ACTIONS.includes(action)) {
      return;
    }

    if (VALUE_ACTIONS.includes(action)) {
      applyValue(action, value, meta);
      return;
    }
    applyToggle(action, value, meta);
  }

  function applyValue(action, value, meta = {}) {
    let nextValue = Number(value);
    if (Number.isNaN(nextValue)) {
      if (typeof value === "boolean") {
        nextValue = value ? 1 : 0;
      } else {
        nextValue = state.values[action];
      }
    }

    const limits = VALUE_LIMITS[action];
    if (limits) {
      nextValue = clamp(nextValue, limits.min, limits.max);
    }

    state.values[action] = nextValue;
    updateValueAction(action);
    deps.ui.setValue(action, nextValue);
    persist(meta);
    emit(action, nextValue);
  }

  function applyToggle(action, value, meta = {}) {
    const previous = state.toggles[action];
    const next = typeof value === "boolean" ? value : !previous;

    if (ACTION_GROUPS.align.includes(action) && next) {
      ACTION_GROUPS.align.forEach((item) => {
        if (item !== action) {
          state.toggles[item] = false;
          updateToggleAction(item);
          deps.ui.setToggle(item, false);
        }
      });
    }
    if (ACTION_GROUPS.chromatic.includes(action) && next) {
      ACTION_GROUPS.chromatic.forEach((item) => {
        if (item !== action) {
          state.toggles[item] = false;
          updateToggleAction(item);
          deps.ui.setToggle(item, false);
        }
      });
    }
    if (ACTION_GROUPS.cursors.includes(action) && next) {
      ACTION_GROUPS.cursors.forEach((item) => {
        if (item !== action) {
          state.toggles[item] = false;
          updateToggleAction(item);
          deps.ui.setToggle(item, false);
        }
      });
    }

    state.toggles[action] = next;
    updateToggleAction(action);
    deps.ui.setToggle(action, next);
    persist(meta);
    emit(action, next);
  }

  function updateValueAction(action) {
    switch (action) {
      case "contentScaling": {
        const value = state.values.contentScaling;
        document.documentElement.style.setProperty(
          "--rl-content-scale",
          String(1 + value / 100),
        );
        toggleHtmlClass(action, value !== 0);
        break;
      }
      case "fontSizing": {
        const value = state.values.fontSizing;
        document.documentElement.style.setProperty("--rl-font-size-delta", `${value}px`);
        toggleBodyClass(action, value !== 0);
        break;
      }
      case "lineHeight": {
        const value = state.values.lineHeight;
        const calculated = 1.6 + value / 100;
        document.documentElement.style.setProperty(
          "--rl-line-height",
          String(Math.max(1, Number(calculated.toFixed(2)))),
        );
        toggleBodyClass(action, value !== 0);
        break;
      }
      case "letterSpacing": {
        const value = state.values.letterSpacing;
        document.documentElement.style.setProperty("--rl-letter-spacing", `${value}px`);
        toggleBodyClass(action, value !== 0);
        break;
      }
      default:
        break;
    }
  }

  function updateToggleAction(action) {
    const active = state.toggles[action];
    toggleHtmlClass(action, active);
    toggleBodyClass(action, active);

    switch (action) {
      case "readingMask":
        active ? enableReadingMask() : disableReadingMask();
        break;
      case "readingGuide":
        active ? enableReadingGuide() : disableReadingGuide();
        break;
      case "hideEmoji":
        active ? deps.emoji.enable() : deps.emoji.disable();
        break;
      case "dyslexiaFont":
        if (active) {
          ensureDyslexiaFaces();
        }
        break;
      default:
        break;
    }
  }

  function applyAll() {
    VALUE_ACTIONS.forEach((action) => {
      updateValueAction(action);
      deps.ui.setValue(action, state.values[action]);
    });
    TOGGLE_ACTIONS.forEach((action) => {
      updateToggleAction(action);
      deps.ui.setToggle(action, state.toggles[action]);
    });
  }

  function reset() {
    VALUE_ACTIONS.forEach((action) => {
      state.values[action] = 0;
      updateValueAction(action);
      deps.ui.setValue(action, 0);
    });
    TOGGLE_ACTIONS.forEach((action) => {
      state.toggles[action] = false;
      updateToggleAction(action);
      deps.ui.setToggle(action, false);
    });
    persist({});
    emit("reset", true);
  }

  function destroy() {
    disableReadingMask();
    disableReadingGuide();
    deps.emoji.disable();

    [...Object.values(HTML_CLASS_ACTIONS)].forEach((className) => {
      document.documentElement.classList.remove(className);
    });
    [...Object.values(BODY_CLASS_ACTIONS)].forEach((className) => {
      document.body.classList.remove(className);
    });

    document.documentElement.style.removeProperty("--rl-content-scale");
    document.documentElement.style.removeProperty("--rl-font-size-delta");
    document.documentElement.style.removeProperty("--rl-line-height");
    document.documentElement.style.removeProperty("--rl-letter-spacing");
    document.documentElement.style.removeProperty("--rl-reading-mask-height");
    document.documentElement.style.removeProperty("--rl-reading-guide-height");

    if (runtime.dyslexiaStyle) {
      runtime.dyslexiaStyle.remove();
      runtime.dyslexiaStyle = null;
    }
  }

  function persist(meta) {
    if (!config.remember || meta.persist === false) {
      return;
    }
    deps.storage.save(snapshot());
  }

  function emit(action, value) {
    const detail = { action, value };
    window.dispatchEvent(new CustomEvent("ReadlyGoActionChanged", { detail }));
    // Backward-compatible event alias.
    window.dispatchEvent(new CustomEvent("ReadablerLiteActionChanged", { detail }));
  }

  function normalizeAction(action) {
    if (!action) {
      return "";
    }
    return toCamelCase(action);
  }

  function toggleHtmlClass(action, active) {
    const className = HTML_CLASS_ACTIONS[action];
    if (!className) {
      return;
    }
    document.documentElement.classList.toggle(className, Boolean(active));
  }

  function toggleBodyClass(action, active) {
    const className = BODY_CLASS_ACTIONS[action];
    if (!className) {
      return;
    }
    document.body.classList.toggle(className, Boolean(active));
  }

  function ensureDyslexiaFaces() {
    if (runtime.dyslexiaStyle) {
      return;
    }

    const base = resolveDyslexiaAssetsBaseUrl();
    if (!base) {
      return;
    }
    const style = document.createElement("style");
    style.id = "rl-dyslexia-font-face";
    if (config.styleNonce) {
      style.setAttribute("nonce", config.styleNonce);
    }
    style.textContent = `
      @font-face {
        font-family: "OpenDyslexic";
        src: url("${base}/font-dislessici/OpenDyslexic-Regular.otf") format("opentype");
        font-style: normal;
        font-weight: 400;
        font-display: swap;
      }
      @font-face {
        font-family: "OpenDyslexic";
        src: url("${base}/font-dislessici/OpenDyslexic-Bold.otf") format("opentype");
        font-style: normal;
        font-weight: 700;
        font-display: swap;
      }
      @font-face {
        font-family: "OpenDyslexic";
        src: url("${base}/font-dislessici/OpenDyslexic-Italic.otf") format("opentype");
        font-style: italic;
        font-weight: 400;
        font-display: swap;
      }
      @font-face {
        font-family: "OpenDyslexic";
        src: url("${base}/font-dislessici/OpenDyslexic-BoldItalic.otf") format("opentype");
        font-style: italic;
        font-weight: 700;
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
    runtime.dyslexiaStyle = style;
    preloadDyslexiaFaces();
  }

  function preloadDyslexiaFaces() {
    if (runtime.dyslexiaPreloadStarted || !document.fonts?.load) {
      return;
    }
    runtime.dyslexiaPreloadStarted = true;
    Promise.all([
      document.fonts.load('400 1em "OpenDyslexic"'),
      document.fonts.load('700 1em "OpenDyslexic"'),
      document.fonts.load('italic 400 1em "OpenDyslexic"'),
      document.fonts.load('italic 700 1em "OpenDyslexic"'),
    ]).catch(() => {});
  }

  function resolveDyslexiaAssetsBaseUrl() {
    const configuredBase = String(config.assetsBaseUrl || "").trim();
    if (configuredBase) {
      return configuredBase.replace(/\/+$/, "");
    }

    const scriptSrc = Array.from(document.querySelectorAll("script[src]"))
      .map((script) => script.getAttribute("src") || "")
      .find((src) => /readlygo(\.umd|\.esm)?\.js/i.test(src) || /\/@caleidoscopioweb\/readlygo@/i.test(src));

    if (!scriptSrc) {
      return "";
    }

    try {
      const url = new URL(scriptSrc, window.location.href);
      const distIndex = url.pathname.lastIndexOf("/dist/");
      if (distIndex >= 0) {
        url.pathname = `${url.pathname.slice(0, distIndex)}/assets`;
      } else {
        const slashIndex = url.pathname.lastIndexOf("/");
        url.pathname = `${url.pathname.slice(0, Math.max(0, slashIndex))}/assets`;
      }
      url.search = "";
      url.hash = "";
      return url.toString().replace(/\/+$/, "");
    } catch {
      return "";
    }
  }

  function enableReadingMask() {
    if (runtime.readingMask.top || runtime.readingMask.bottom) {
      return;
    }

    const top = document.createElement("div");
    top.id = "rl-reading-mask-top";
    const bottom = document.createElement("div");
    bottom.id = "rl-reading-mask-bottom";
    document.body.appendChild(top);
    document.body.appendChild(bottom);
    runtime.readingMask.top = top;
    runtime.readingMask.bottom = bottom;

    document.documentElement.style.setProperty(
      "--rl-reading-mask-height",
      `${config.readingMaskHeight}px`,
    );

    const handler = (event) => {
      const centerY = event.clientY;
      const half = config.readingMaskHeight / 2;
      const topHeight = Math.max(0, centerY - half);
      const bottomTop = Math.min(window.innerHeight, centerY + half);
      top.style.top = "0";
      top.style.height = `${topHeight}px`;
      bottom.style.top = `${bottomTop}px`;
      bottom.style.bottom = "0";
    };
    runtime.readingMask.handler = handler;
    document.addEventListener("mousemove", handler);
  }

  function disableReadingMask() {
    if (runtime.readingMask.handler) {
      document.removeEventListener("mousemove", runtime.readingMask.handler);
      runtime.readingMask.handler = null;
    }
    if (runtime.readingMask.top) {
      runtime.readingMask.top.remove();
      runtime.readingMask.top = null;
    }
    if (runtime.readingMask.bottom) {
      runtime.readingMask.bottom.remove();
      runtime.readingMask.bottom = null;
    }
  }

  function enableReadingGuide() {
    if (runtime.readingGuide.node) {
      return;
    }
    document.documentElement.style.setProperty(
      "--rl-reading-guide-height",
      `${config.readingGuideHeight}px`,
    );
    const guide = document.createElement("div");
    guide.id = "rl-reading-guide";
    document.body.appendChild(guide);
    runtime.readingGuide.node = guide;
    const half = config.readingGuideHeight / 2;
    const handler = (event) => {
      guide.style.top = `${Math.max(0, event.clientY - half)}px`;
    };
    runtime.readingGuide.handler = handler;
    document.addEventListener("mousemove", handler);
  }

  function disableReadingGuide() {
    if (runtime.readingGuide.handler) {
      document.removeEventListener("mousemove", runtime.readingGuide.handler);
      runtime.readingGuide.handler = null;
    }
    if (runtime.readingGuide.node) {
      runtime.readingGuide.node.remove();
      runtime.readingGuide.node = null;
    }
  }

  function getState() {
    return snapshot();
  }

  return {
    load,
    applyAll,
    dispatch,
    reset,
    getState,
    destroy,
  };
}
